import React, { useEffect, useRef, useState } from 'react';

type Props = {
  children: React.ReactNode;
  // Altura máxima (px) en estado colapsado.
  collapsedMaxHeight?: number;
  // Altura del fade al pie (clase Tailwind, p.ej. 'h-14' | 'h-10').
  fadeHeightClass?: string;
  isSpanish?: boolean;
  // Solo en DESKTOP (lg:): sin fade, sin padding inferior extra y el botón pegado al borde
  // inferior (superpuesto al divisor entre secciones). Mobile/tablet no cambian (siguen con
  // fade + botón flotante). Lo usa Amenidades (contenido informativo, filas completas).
  desktopFlush?: boolean;
};

// Efecto editorial estilo Luma, REUTILIZABLE (lo usan "Sobre el lugar" y "Amenidades"):
//  • altura máxima FIJA en colapsado (el contenido se corta después),
//  • transición SUAVE de max-height al expandir/contraer,
//  • fade/gradiente al pie que desvanece progresivamente lo último,
//  • botón circular flotante centrado SOBRE el borde inferior (sobresale un poco), con flecha
//    abajo→arriba. Sin texto "Mostrar más": la interacción es el botón.
// Si el contenido no supera la altura máxima, no hay fade ni botón.
export default function CollapsibleFade({
  children,
  collapsedMaxHeight = 110,
  fadeHeightClass = 'h-14',
  isSpanish = true,
  desktopFlush = false,
}: Props): React.ReactElement {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const [fullHeight, setFullHeight] = useState(0);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const measure = () => {
      const h = el.scrollHeight;
      setFullHeight(h);
      setOverflows(h > collapsedMaxHeight + 4);
    };
    measure();
    const ro =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [collapsedMaxHeight]);

  const maxHeight = expanded ? fullHeight || collapsedMaxHeight : collapsedMaxHeight;

  return (
    <div className={`group relative ${overflows ? (desktopFlush ? 'pb-4 lg:pb-0' : 'pb-4') : ''}`}>
      <div
        ref={contentRef}
        className="relative overflow-hidden transition-[max-height] duration-500 ease-in-out"
        style={{ maxHeight: `${maxHeight}px` }}
      >
        {children}

        {overflows && (
          <div
            aria-hidden="true"
            className={`pointer-events-none absolute inset-x-0 bottom-0 ${fadeHeightClass} ${
              desktopFlush ? 'lg:hidden' : ''
            } bg-gradient-to-t from-[#f5f7fa] via-[#f5f7fa]/85 to-transparent transition-opacity duration-300 ${
              expanded ? 'opacity-0' : 'opacity-100'
            }`}
          />
        )}
      </div>

      {overflows && (
        <div className={`pointer-events-none absolute inset-x-0 bottom-4 ${
          desktopFlush ? 'lg:bottom-0' : ''
        } flex justify-center translate-y-1/2 opacity-0 transition-opacity duration-200 group-hover:opacity-100 focus-within:opacity-100`}>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-label={
              expanded
                ? isSpanish ? 'Mostrar menos' : 'Show less'
                : isSpanish ? 'Mostrar más' : 'Show more'
            }
            className="pointer-events-auto w-8 h-8 rounded-full bg-white border border-gray-200 text-gray-600 flex items-center justify-center shadow-sm hover:text-tikipal-orange hover:border-tikipal-orange/40 active:scale-95 transition-all"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`}
              aria-hidden="true"
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}
